
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import {
  ThemeProvider,
  useTheme
} from '@mui/material/styles';
import { Box, useMediaQuery, AppBar, Toolbar, IconButton, CircularProgress, Typography, Paper, CssBaseline } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { ColorModeProvider, useColorMode } from './context/ColorModeContext';
import { getTheme } from './theme.js';

import Sidebar from './components/Sidebar/Sidebar.jsx';
import ProtectedRoute from './components/Auth/ProtectedRoute.js'; // Import the ProtectedRoute
import FloatingAddButton from './components/FloatingAddButton/FloatingAddButton.jsx';
import CalendarPage from './pages/CalendarPage';

import LoginPage from './pages/LoginPage.js';
import ForgotPasswordPage from './pages/ForgotPasswordPage.js';
import ResetPasswordPage from './pages/ResetPasswordPage.js';
import { PAGE_ZOOM, isZoomedPagePath, isPhoneUnzoomedPath } from './utils/pageZoom';

import { TeamspaceProvider, useTeamspace } from './context/TeamspaceProvider';
import { AuthProvider } from './context/AuthContext';
import { PresenceProvider } from './context/PresenceContext';
import { useAuth } from './context/AuthContext';
import { DocumentProgressProvider } from './context/DocumentProgressContext';
import DocumentProgressPanel from './components/DocumentProgressPanel';
import { InvoicingBadgeProvider } from './context/InvoicingBadgeContext';
import React, { Suspense, useEffect, useState } from 'react';
import { lazyWithReload } from './utils/lazyWithReload';
import Header from './components/Header/Header.jsx';
import { SHORT_TOUCH_QUERY, TOUCH_TABLET_QUERY, useNoPageZoom } from './hooks/useIsPhone';

// Route-level code splitting. Every page used to be imported eagerly, so all of
// them — InvoicingPage alone is ~13k lines — shipped in the one main bundle that
// every user downloads on first load and after every deploy. Calendar (the
// default landing page) and the auth pages stay eager so the first screen never
// waits on a second request; the rest load the first time they are opened.
const WorkSummaryPage = lazyWithReload(() => import('./pages/WorkSummaryPage.js'));
const TrackingSummaryOptimized = lazyWithReload(() => import('./pages/TrackingSummaryOptimized.js'));
const ClientsListPage = lazyWithReload(() => import('./pages/ClientsListPage.js'));
const ProjectsListPage = lazyWithReload(() => import('./pages/ProjectsListPage.js'));
const TimeEntriesListPage = lazyWithReload(() => import('./pages/TimeEntriesListPage.js'));
const MembersListPage = lazyWithReload(() => import('./pages/MembersListPage.js'));
const UserProfilePage = lazyWithReload(() => import('./pages/UserProfilePage.js'));
const ProjectMembersPage = lazyWithReload(() => import('./pages/ProjectMembersPage.js'));
const TagsListPage = lazyWithReload(() => import('./pages/TagsListPage.js'));
const DeveloperAdminPage = lazyWithReload(() => import('./pages/DeveloperAdminPage.js'));
const InvoicingPage = lazyWithReload(() => import('./pages/InvoicingPage.js'));
const QuotationPage = lazyWithReload(() => import('./pages/QuotationPage.js'));
const SalesDashboardPage = lazyWithReload(() => import('./pages/SalesDashboardPage.js'));

// Shown while a lazily loaded page's chunk downloads — normally a few hundred ms,
// once per page per deploy, after which the browser cache serves it.
function PageLoadingFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress />
    </Box>
  );
}
// Removed MenuIcon import and drawerWidth constant


function ThemedApp() {
  const { mode } = useColorMode();
  const activeTheme = React.useMemo(() => getTheme(mode), [mode]);
  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <AppContent />
    </ThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TeamspaceProvider>
          <DocumentProgressProvider>
            <InvoicingBadgeProvider>
              <ColorModeProvider>
                {/* One presence socket per tab: header avatars + table row markers. */}
                <PresenceProvider>
                  <ThemedApp />
                </PresenceProvider>
              </ColorModeProvider>
            </InvoicingBadgeProvider>
          </DocumentProgressProvider>
        </TeamspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const { selectedTeamspaceId, setSelectedTeamspaceId, teamspaces, timeEntryMutationLoading } = useTeamspace();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  // Sideways phones (SHORT_TOUCH_QUERY) and touch tablets (TOUCH_TABLET_QUERY) too: a
  // 932px landscape phone or a 1024px iPad is not a desktop. Mouse desktops never match.
  const isMobile = useMediaQuery(`${theme.breakpoints.down('md')}, ${SHORT_TOUCH_QUERY}, ${TOUCH_TABLET_QUERY}`);
  const noPageZoom = useNoPageZoom();
  const [mobileOpen, setMobileOpen] = useState(false);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Load from localStorage if available, otherwise default to 240
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseInt(saved, 10) : 240;
  });

  useEffect(() => {
    localStorage.setItem('sidebarWidth', sidebarWidth.toString());
  }, [sidebarWidth]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Mobile: picking a page in the drawer closes it. (`mobileOpen` is unused on desktop.)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  // Removed sidebarOpen state and handleSidebarToggle function

  // Sync teamspace ID from URL to state
  useEffect(() => {
    if (!isAuthenticated || !teamspaces.length) return;
    
    // Extract teamspaceId from URL
    const pathMatch = location.pathname.match(/^\/(\d+)\//);
    if (pathMatch) {
      const urlTeamspaceId = parseInt(pathMatch[1], 10);
      // Only update if it's different and the teamspace exists
      if (urlTeamspaceId !== selectedTeamspaceId && teamspaces.some(ts => ts.id === urlTeamspaceId)) {
        setSelectedTeamspaceId(urlTeamspaceId);
      }
    }
  }, [location.pathname, isAuthenticated, teamspaces, selectedTeamspaceId, setSelectedTeamspaceId]);

  // Check if we're on the login page
  const isLoginPage = location.pathname === '/login';
  // Public routes that don't require auth and shouldn't trigger redirects
  const isForgotPasswordPage = location.pathname === '/forgot_password';
  const isResetPasswordPage = location.pathname.startsWith('/reset_password/');
  const isPublicRoute = isLoginPage || isForgotPasswordPage || isResetPasswordPage;

  // Redirect unauthenticated users to login page
  useEffect(() => {
    if (!loading && !isAuthenticated && !isPublicRoute) {
      navigate('/login', { replace: true });
    }
  }, [loading, isAuthenticated, isPublicRoute, navigate]);

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (!loading && isAuthenticated && isLoginPage) {
      navigate(`/${selectedTeamspaceId || 'default'}/calendar`, { replace: true });
    }
  }, [loading, isAuthenticated, isLoginPage, navigate, selectedTeamspaceId]);

  // Render public routes separately without authenticated layout
  if (isPublicRoute) {
    return (
      <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', position: 'relative', width: '100%' }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot_password" element={<ForgotPasswordPage />} />
          <Route path="/reset_password/:uid/:token" element={<ResetPasswordPage />} />
          {/* Catch-all route for unauthenticated users */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    );
  }

  // Render nothing or a loading indicator while authentication status is being determined (only for non-login pages)
  if (loading) {
    return null; // Or a loading spinner
  }
  
  // Authenticated routes
  return (
    <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', position: 'relative', width: '100%' }}>
      {timeEntryMutationLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(2px)',
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              minWidth: 260,
            }}
          >
            <CircularProgress />
            <Typography variant="subtitle1">
              Processing time entry…
            </Typography>
          </Paper>
        </Box>
      )}
      {isAuthenticated && <FloatingAddButton />}
      {isAuthenticated && <DocumentProgressPanel />}
      <Box sx={{ display: 'flex', minHeight: '100vh', mt: isMobile ? '0px' : 0 }}> {/* Removed margin-top for AppBar */}
      {isAuthenticated && (
          <Sidebar 
            mobileOpen={mobileOpen} 
            handleDrawerToggle={handleDrawerToggle}
            width={sidebarWidth}
            onWidthChange={setSidebarWidth}
            isMobile={isMobile}
          />
        )}
        <Box className='app' sx={{ flex: 1, minWidth: 0, overflow: 'hidden', ml: isMobile ? 0 : (isAuthenticated ? `${sidebarWidth}px` : 0) }}>
          {/* The desktop header always renders at PAGE_ZOOM, so it is the same size on every
              page as on the zoomed ones (utils/pageZoom.js). The mobile top bar only shrinks
              on the zoomed pages — except on phones and touch tablets, where those pages
              drop their zoom (isPhoneUnzoomedPath). */}
          {isAuthenticated && (
            <Header zoom={!isMobile || (isZoomedPagePath(location.pathname) && !(noPageZoom && isPhoneUnzoomedPath(location.pathname))) ? PAGE_ZOOM : undefined} onMenuClick={handleDrawerToggle} />
          )}
          <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
          <Route element={<ProtectedRoute />}> 
              <Route path="/:teamspaceId/time_entries" element={<TimeEntriesListPage />} />
              <Route path="/:teamspaceId/calendar" element={<CalendarPage />} />
              <Route path="/:teamspaceId/tracking-summary" element={<TrackingSummaryOptimized />} />
              <Route path="/:teamspaceId/work-summary" element={<WorkSummaryPage />} />
              <Route path="/:teamspaceId/clients" element={<ClientsListPage />} />
              <Route path="/:teamspaceId/projects" element={<ProjectsListPage />} />
              <Route path="/:teamspaceId/members" element={<MembersListPage />} />
              <Route path="/:teamspaceId/tags" element={<TagsListPage />} />
              <Route path="/:teamspaceId/profile" element={<UserProfilePage />} />
              <Route path="/:teamspaceId/projects/:projectId" element={<ProjectMembersPage />} />
              <Route path="/:teamspaceId/developer-admin" element={<DeveloperAdminPage />} />
              <Route path="/:teamspaceId/invoicing" element={<InvoicingPage />} />
              <Route path="/:teamspaceId/quotation" element={<QuotationPage />} />
              <Route path="/:teamspaceId/sales" element={<SalesDashboardPage />} />
              {/* Redirect root to default teamspace time entries */}
              <Route path="/" element={<Navigate to={`/${selectedTeamspaceId || 'default'}/calendar`} replace />} />
              {/* Catch-all route for authenticated users - redirect to time entries */}
              <Route path="*" element={<Navigate to={`/${selectedTeamspaceId || 'default'}/calendar`} replace />} />
            </Route>
          </Routes>
          </Suspense>
        </Box>
      </Box>
    </div>
  );
}

// Removed TeamspaceRedirect as its logic is now implicitly handled or redundant
// function TeamspaceRedirect() {
//   const { teamspaces, loading } = useTeamspace();
//   const { isAuthenticated } = useAuth(); 

//   if (loading) {
//     return <div>Loading...</div>; 
//   }

//   if (isAuthenticated && teamspaces.length > 0) {
//     return <Navigate to={`/tracking-summary`} replace />;
//   }

//   return <Navigate to="/login" replace />;
// }

export default App;
